import { Braces, Bug, Check, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { CopyIconButton } from '~/components/common/copy-icon-button';
import { ExternalLinkIconButton } from '~/components/common/external-link-icon-button';
import { Button } from '~/components/ui/button';
import { copyToClipboard } from '~/utils/clipboard';

/** One id row in the debug panel: an API field name and its value. */
export interface DebugIdEntry {
  /** Raw API field name (e.g. `workflowId`) — shown verbatim, not translated. */
  label: string;
  value: string | null | undefined;
  /** Optional deep-link out to another tool (e.g. the Temporal Web UI for a
   *  workflow id). When set, an external-link button renders beside the copy
   *  button; absent/null leaves the row copy-only. */
  href?: string | null;
}

interface BatchDebugPanelProps {
  open: boolean;
  onClose: () => void;
  ids: DebugIdEntry[];
  /** Panel heading — defaults to the shared "Raw IDs" copy. */
  title?: string;
  /** Sub-heading under the title — defaults to the batch-detail copy. Pass a
   *  caller-specific line when the ids come from another response (e.g. an
   *  enrichment session rather than a batch). */
  subtitle?: string;
  /** Automation hook for the panel root; child ids derive from it. Defaults to `debug-panel`. */
  testId?: string;
}

/**
 * Super-admin debug aid — a floating card pinned to the bottom-right that lists
 * the raw identifiers from a detail response, each click-to-copy. Non-modal
 * (doesn't block the page) and `fixed` (unaffected by page scroll). Gated by the
 * `debugger-int` flag + super-admin at the call site; a troubleshooting tool, so
 * the field names render verbatim (untranslated).
 */
export function BatchDebugPanel({
  open,
  onClose,
  ids,
  title,
  subtitle,
  testId = 'debug-panel',
}: BatchDebugPanelProps) {
  const { t } = useTranslation('catalog-operations');
  const [copiedAll, setCopiedAll] = useState(false);
  const heading = title ?? t('debugPanel.title');
  const subheading = subtitle ?? t('debugPanel.subtitle');

  // Copy every id as one pretty-printed JSON object — the fast path for pasting
  // the whole set into a ticket. Missing values serialize as null so the shape
  // mirrors the raw response.
  function handleCopyAll() {
    const payload = Object.fromEntries(ids.map((row) => [row.label, row.value ?? null]));
    copyToClipboard(JSON.stringify(payload, null, 2));
    setCopiedAll(true);
    toast.success(t('debugPanel.copiedAll'));
    setTimeout(() => {
      setCopiedAll(false);
    }, 1500);
  }

  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-label={heading}
      data-testid={testId}
      className="fixed right-4 bottom-4 z-50 w-[min(24rem,calc(100vw-2rem))]"
    >
      <div className="bg-popover text-popover-foreground flex max-h-[70vh] flex-col overflow-hidden rounded-xl border shadow-lg">
        <div className="flex shrink-0 items-start justify-between gap-2 border-b px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className="bg-primary/10 text-primary flex h-7 w-7 shrink-0 items-center justify-center rounded-lg">
              <Bug aria-hidden="true" className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="text-sm leading-tight font-semibold">{heading}</p>
              <p className="text-muted-foreground text-xs">{subheading}</p>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 cursor-pointer"
            aria-label={t('debugPanel.close')}
            data-testid={`${testId}-close-button`}
            onClick={onClose}
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </Button>
        </div>

        <div className="min-h-0 flex-1 divide-y overflow-y-auto">
          {ids.map((row) => (
            <div
              key={row.label}
              data-testid={`${testId}-item-${row.label}`}
              className="flex items-center justify-between gap-3 px-4 py-2.5"
            >
              <div className="flex min-w-0 flex-col gap-0.5">
                {/* Full-contrast, semibold label so the field name reads clearly
                    as a key and doesn't blend into the mono value below it.
                    Kept in its natural camelCase — these are raw API field names. */}
                <span className="text-foreground text-xs font-semibold">{row.label}</span>
                <span
                  data-testid={`${testId}-item-value-${row.label}`}
                  className="text-muted-foreground truncate font-mono text-xs"
                  title={row.value ?? undefined}
                >
                  {row.value ?? t('debugPanel.empty')}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                {/* Temporal (or other tool) deep-link — only for rows that carry
                    an href; self-hides otherwise, so non-workflow rows stay
                    copy-only. */}
                <ExternalLinkIconButton
                  href={row.href}
                  label={t('debugPanel.openInTemporal', { field: row.label })}
                  testId={`${testId}-item-link-${row.label}`}
                />
                <CopyIconButton
                  text={row.value ?? ''}
                  label={t('debugPanel.copy', { field: row.label })}
                  copyMessage={t('debugPanel.copied', { field: row.label })}
                  testId={`${testId}-item-copy-button-${row.label}`}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="shrink-0 border-t px-4 py-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full cursor-pointer"
            onClick={handleCopyAll}
            data-testid={`${testId}-copy-all-button`}
          >
            {copiedAll ? (
              <Check className="text-success h-4 w-4" aria-hidden="true" />
            ) : (
              <Braces className="h-4 w-4" aria-hidden="true" />
            )}
            {t('debugPanel.copyAll')}
          </Button>
        </div>
      </div>
    </div>
  );
}
